import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Bell, Clock, Mail, Settings } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/app/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface Notification {
  id: string;
  title: string;
  message: string;
  time: string;
  read: boolean;
  type: string;
  avatar?: string;
  created_at: string;
  user_id: string;
  is_read: boolean;
  action_url?: string;
}

const Notifications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const isPro = user?.plan === 'pro';
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  
  // Notification settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [reminderTime, setReminderTime] = useState("30");
  
  // Fetch real notifications if user is authenticated
  useEffect(() => {
    const fetchNotifications = async () => {
      if (user && user.id) {
        try {
          setLoading(true);
          
          // Fetch actual notifications from Supabase
          const { data, error } = await supabase
            .from('ft_notifications')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });
            
          if (error) {
            console.error("Error fetching notifications:", error);
            toast({
              title: "Error",
              description: "Failed to load notifications",
              variant: "destructive"
            });
            setLoading(false);
            return;
          }
          
          if (data && data.length > 0) {
            // Transform database notifications to our UI format
            const transformedNotifications = data.map(notification => ({
              id: notification.id,
              title: notification.type?.charAt(0).toUpperCase() + notification.type?.slice(1).replace('_', ' ') || "Notification",
              message: notification.message,
              time: formatTimeAgo(new Date(notification.created_at)),
              read: notification.is_read,
              type: notification.type || "notification",
              avatar: "",
              created_at: notification.created_at,
              user_id: notification.user_id,
              is_read: notification.is_read,
              action_url: notification.action_url
            }));
            
            setNotifications(transformedNotifications);
          } else {
            // No notifications, set empty array
            setNotifications([]);
          }
          
          setLoading(false);
        } catch (error) {
          console.error("Error in fetchNotifications:", error);
          setLoading(false);
        }
      }
    };
    
    fetchNotifications();
    
    // Also fetch user notification preferences
    const fetchNotificationPreferences = async () => {
      if (user && user.id) {
        try {
          const { data, error } = await supabase
            .from('ft_user_preferences')
            .select('*')
            .eq('user_id', user.id)
            .single();
            
          if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned" error
            console.error("Error fetching notification preferences:", error);
            return;
          }
          
          if (data) {
            // Set notification preferences from database
            setEmailNotifications(data.email_notifications ?? true);
            setPushNotifications(data.push_notifications ?? true);
            setReminderTime(data.reminder_time?.toString() ?? "30");
          }
        } catch (error) {
          console.error("Error in fetchNotificationPreferences:", error);
        }
      }
    };
    
    fetchNotificationPreferences();
  }, [user, toast]);
  
  // Helper function to format time ago
  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.round(diffMs / 1000);
    const diffMin = Math.round(diffSec / 60);
    const diffHour = Math.round(diffMin / 60);
    const diffDay = Math.round(diffHour / 24);
    
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    
    return date.toLocaleDateString();
  };
  
  const markAllAsRead = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Update all notifications in the database
      const { error } = await supabase
        .from('ft_notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);
        
      if (error) {
        console.error("Error marking notifications as read:", error);
        toast({
          title: "Error",
          description: "Failed to mark notifications as read",
          variant: "destructive"
        });
        return;
      }
      
      // Update local state
      setNotifications(current => 
        current.map(notif => ({ ...notif, read: true, is_read: true }))
      );
      
      toast({
        title: "All notifications marked as read",
        description: "Your notification inbox has been cleared."
      });
    } catch (err) {
      console.error("Error in markAllAsRead:", err);
      toast({
        title: "Error",
        description: "Failed to update notifications",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const saveSettings = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Save notification preferences to database
      const { error } = await supabase
        .from('ft_user_preferences')
        .upsert({
          user_id: user.id,
          email_notifications: emailNotifications,
          push_notifications: pushNotifications,
          reminder_time: parseInt(reminderTime)
        }, {
          onConflict: 'user_id'
        });
        
      if (error) {
        console.error("Error saving notification preferences:", error);
        toast({
          title: "Error",
          description: "Failed to save notification preferences",
          variant: "destructive"
        });
        return;
      }
      
      toast({
        title: "Settings Saved",
        description: "Your notification preferences have been updated."
      });
    } catch (err) {
      console.error("Error in saveSettings:", err);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const getNotificationTypeIcon = (type: string) => {
    switch (type) {
      case "reminder":
      case "event_reminder":
        return <Bell className="h-4 w-4 text-blue-500" />;
      case "invite":
      case "invite_sent":
      case "invite_received":
        return <Bell className="h-4 w-4 text-green-500" />;
      case "update":
      case "event_update":
        return <Bell className="h-4 w-4 text-orange-500" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };
  
  // Handle clicking on a notification
  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      try {
        // Mark notification as read in database
        await supabase
          .from('ft_notifications')
          .update({ is_read: true })
          .eq('id', notification.id);
          
        // Update local state
        setNotifications(current => 
          current.map(n => n.id === notification.id ? { ...n, read: true, is_read: true } : n)
        );
      } catch (err) {
        console.error("Error marking notification as read:", err);
      }
    }
    
    // Navigate to action URL if provided
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-black">Notifications</h1>
        </div>
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={markAllAsRead}
          disabled={loading || notifications.every(n => n.read)}
          className="bg-blue-50 hover:bg-blue-100 text-blue-700"
        >
          Mark all as read
        </Button>
      </div>
      
      <Tabs defaultValue="inbox">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="inbox">Inbox</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
        
        <TabsContent value="inbox" className="mt-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : notifications.length > 0 ? (
            <div className="space-y-4">
              {notifications.map((notification) => (
                <Card 
                  key={notification.id}
                  className={`${notification.read ? "opacity-75" : "border-blue-200 shadow-sm"} cursor-pointer hover:bg-gray-50`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="mt-1">
                        {getNotificationTypeIcon(notification.type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-black">{notification.title}</p>
                            <p className="text-sm text-gray-600">{notification.message}</p>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-xs text-gray-500">{notification.time}</span>
                            {!notification.read && (
                              <Badge variant="default" className="mt-1 bg-blue-600">
                                New
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center p-12 bg-gray-50 rounded-lg">
              <Bell className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No notifications</h3>
              <p className="mt-2 text-sm text-gray-500">
                You don't have any notifications right now. We'll notify you when something arrives.
              </p>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="settings" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-black">
                <Settings className="h-5 w-5" />
                Notification Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-medium text-black">Delivery Methods</h3>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="flex items-center cursor-pointer text-black">
                      <Mail className="mr-2 h-4 w-4 text-blue-500" />
                      Email Notifications
                    </Label>
                    <p className="text-xs text-gray-500">Get notifications delivered to your email</p>
                  </div>
                  <Switch 
                    checked={emailNotifications}
                    onCheckedChange={setEmailNotifications}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="flex items-center cursor-pointer text-black">
                      <Bell className="mr-2 h-4 w-4 text-blue-500" />
                      Push Notifications
                    </Label>
                    <p className="text-xs text-gray-500">Get notifications in your browser</p>
                  </div>
                  <Switch 
                    checked={pushNotifications}
                    onCheckedChange={setPushNotifications}
                  />
                </div>
              </div>
              
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-black">Event Reminders</h3>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="flex items-center text-black">
                      <Clock className="mr-2 h-4 w-4 text-blue-500" />
                      Reminder Time
                    </Label>
                    <p className="text-xs text-gray-500">When to send event reminders</p>
                  </div>
                  <Select
                    value={reminderTime}
                    onValueChange={setReminderTime}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 minutes before</SelectItem>
                      <SelectItem value="15">15 minutes before</SelectItem>
                      <SelectItem value="30">30 minutes before</SelectItem>
                      <SelectItem value="60">1 hour before</SelectItem>
                      <SelectItem value="1440">1 day before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {!isPro && (
                  <div className="rounded-md bg-yellow-50 p-4 border border-yellow-200">
                    <p className="text-sm text-yellow-700">
                      <span className="font-medium">Pro Feature Alert:</span> Advanced notification 
                      options including SMS notifications are available with our Pro plan.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2 bg-white"
                      onClick={() => navigate('/pricing')}
                    >
                      Upgrade to Pro
                    </Button>
                  </div>
                )}
              </div>
              
              <Button onClick={saveSettings} className="bg-blue-600 hover:bg-blue-700">
                <span className="text-white">Save Settings</span>
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Notifications;
